// Splash.tsx
'use client';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
export function Splash({ isReady, minDurationMs = 300, delayBeforeExitMs = 0, fadeDurationMs = 200, waitForOverlayExit = true, maxOverlayExitMs = 2000, restartKey, className = '', renderOverlay, children, }) {
    const [phase, setPhase] = useState('pending');
    const [shouldExit, setShouldExit] = useState(false);
    const [overlayOpacity, setOverlayOpacity] = useState(1); // container crossfade
    const startRef = useRef(null);
    const aliveRef = useRef(true);
    // allow overlay to signal completion
    const exitResolveRef = useRef(null);
    const onExitComplete = () => {
        exitResolveRef.current?.();
        exitResolveRef.current = null;
    };
    // restart cycle when key changes
    useEffect(() => {
        aliveRef.current = true;
        startRef.current = performance.now();
        setPhase('pending');
        setShouldExit(false);
        setOverlayOpacity(1);
        return () => {
            aliveRef.current = false;
        };
    }, [restartKey]);
    // main lifecycle
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (startRef.current == null)
                startRef.current = performance.now();
            if (!isReady) {
                // stay pending until parent is ready
                if (phase !== 'pending')
                    setPhase('pending');
                return;
            }
            // enforce min duration
            const elapsed = performance.now() - (startRef.current ?? performance.now());
            const waitMin = Math.max(0, minDurationMs - elapsed);
            if (waitMin)
                await new Promise((r) => setTimeout(r, waitMin));
            if (cancelled || !aliveRef.current)
                return;
            // optional “give the overlay a beat to compose itself” delay
            if (delayBeforeExitMs > 0) {
                await new Promise((r) => setTimeout(r, delayBeforeExitMs));
                if (cancelled || !aliveRef.current)
                    return;
            }
            // tell overlay to start its exit animation
            setPhase('exiting');
            setShouldExit(true);
            if (waitForOverlayExit) {
                await new Promise((resolve) => {
                    exitResolveRef.current = resolve;
                    // safety timeout
                    setTimeout(() => {
                        if (exitResolveRef.current) {
                            exitResolveRef.current();
                            exitResolveRef.current = null;
                        }
                    }, maxOverlayExitMs);
                });
            }
            if (cancelled || !aliveRef.current)
                return;
            // crossfade the container itself
            setPhase('fading');
            if (fadeDurationMs > 0) {
                setOverlayOpacity(0);
                await new Promise((r) => setTimeout(r, fadeDurationMs));
            }
            if (cancelled || !aliveRef.current)
                return;
            setPhase('completed');
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isReady,
        minDurationMs,
        delayBeforeExitMs,
        fadeDurationMs,
        waitForOverlayExit,
        maxOverlayExitMs,
        restartKey,
    ]);
    // Single render tree: content underneath, overlay on top until completed
    return (_jsx(_Fragment, { children: phase !== 'completed' ? (_jsx("div", { "aria-busy": phase !== 'fading', className: `fixed inset-0 z-[9999] grid h-dvh place-items-center bg-background transition-opacity ${className}`, style: {
                opacity: overlayOpacity,
                transitionDuration: `${fadeDurationMs}ms`,
                pointerEvents: phase === 'fading' ? 'none' : 'auto',
            }, children: renderOverlay({ phase, shouldExit, onExitComplete }) })) : (children) }));
}
